import { useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { ThresholdRowModel } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";

export default function useMttrScoring(refetchReleaseStatus: () => void) {
  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.mttr,
  );

  const [thresholds, setThresholds] = useState<number[]>(() => THRESHOLDS.map(() => 0));

  useEffect(() => {
    const anchors = scoringConfig?.config.anchors;
    if (!anchors?.length) return;

    setThresholds(
      THRESHOLDS.map((t) => {
        const match = anchors.find((anchor) => anchor[1] === t.score);
        return match ? match[0] : 0;
      }),
    );
  }, [scoringConfig]);

  const handleThresholdChange = (index: number, value: number) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSave = () => {
    saveScoringConfig(
      { anchors: THRESHOLDS.map((def, i) => [thresholds[i], def.score]) },
      {
        onSuccess: () => {
          refetchReleaseStatus();
          displayToast({ title: "Saved successfully", content: "Your scoring configuration is saved." });
        },
        onError: () => {
          displayToast({ title: "Something went wrong", content: "Please try again." });
        },
      },
    );
  };

  const thresholdRows: ThresholdRowModel[] = THRESHOLDS.map((def, i) => ({
    score: def.score,
    label: def.label,
    value: thresholds[i],
    error: getValidationError(i, thresholds),
    onChange: (v: number) => handleThresholdChange(i, v),
    description: getDescription(def),
  }));

  return {
    thresholdRows,
    scoringConfigLoading,
    scoringConfigError,
    handleSave,
    isSaving,
  };
}

interface ThresholdConfig {
  score: number;
  default: number;
  label: string;
  range?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const THRESHOLDS: ThresholdConfig[] = [
  { score: 10, default: 1, label: "perfect", isFirst: true },
  { score: 8, default: 3, label: "excellent", range: "8.0 up to 9.9" },
  { score: 6, default: 7, label: "good", range: "6.0 up to 7.9" },
  { score: 4, default: 14, label: "warning", range: "4.0 up to 5.9" },
  { score: 2, default: 30, label: "concerning", range: "2.0 up to 3.9" },
  { score: 0, default: 45, label: "critical", range: "0.0 up to 1.9", isLast: true },
];

const formatScoreLabel = (score: number): string => (score === 10 ? "10" : `${score}.0`);

const getDescription = (def: ThresholdConfig): string => {
  if (def.isFirst) {
    return `You consider this MTTR as the upper threshold for a '${def.label}' score (${def.score}). Anything quicker also scores 10.   ←   System default: ${def.default} day.`;
  }
  if (def.isLast) {
    return `You consider this MTTR as the upper threshold for a '${def.label}' score (${def.range}). Anything slower gets a 0 score.  ←   System default: ${def.default} days.`;
  }
  return `You consider this MTTR as the upper threshold for a '${def.label}' score (${def.range}). ← System default: ${def.default} days.`;
};

const getValidationError = (index: number, values: number[]): string | undefined => {
  const value = values[index];
  const prevValue = index > 0 ? values[index - 1] : undefined;
  const nextValue = index < values.length - 1 ? values[index + 1] : undefined;

  const tooSmall = prevValue !== undefined && value <= prevValue;
  const tooBig = nextValue !== undefined && value >= nextValue;

  if (!tooSmall && !tooBig) return undefined;

  if (nextValue === undefined) {
    const prevScore = THRESHOLDS[index - 1].score;
    return `Must be greater than ${prevValue} days (${formatScoreLabel(prevScore)} score threshold).`;
  }
  if (prevValue === undefined) {
    const nextScore = THRESHOLDS[index + 1].score;
    return `Must be less than ${nextValue} days (${formatScoreLabel(nextScore)} score threshold).`;
  }

  const prevScore = THRESHOLDS[index - 1].score;
  const nextScore = THRESHOLDS[index + 1].score;
  return `Must be between ${prevValue} days (${formatScoreLabel(
    prevScore,
  )} score threshold) and ${nextValue} days (${formatScoreLabel(nextScore)} score threshold).`;
};
